import React from "react";
import { Flex, Form, Select, Button, DatePicker, Input } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
const { Option } = Select;

const CriteriaRow = ({ criteria = {}, onUpdate, onDelete, columnNames = [], operators = [], metadata = {}, onColumnChange, isMetaDataLoading, isColumnLoading, columnTypes = {} }) => {
    const { RangePicker } = DatePicker;

    const getDateValues = () => {
        if (!criteria.values || !criteria.values.length) return [null, null];
        return criteria.values
            .map(dateStr => dateStr && dateStr.trim() ? dayjs(dateStr) : null);
    };
    

    const handleDateChange = (dates, dateStrings) => {
        if (dates && dates.length === 1 && dates[0]) {
            const singleDate = dateStrings[0];
            onUpdate('values', [singleDate, singleDate]);
            criteria.values = [singleDate, singleDate];
        } else {
            onUpdate('values', dateStrings);
            criteria.values = dateStrings;
        }
    };

    const shouldShowValueInput = (operator) => {
        return !['IS NULL', 'IS NOT NULL'].includes(operator);
    };

    const shouldShowRangeInput = (operator) => {
        return ['BETWEEN', 'NOT BETWEEN'].includes(operator);
    };

    const renderValueInput = () => {
        if (criteria.operator && !shouldShowValueInput(criteria.operator)) {
            return null;
        }

        if (columnTypes[criteria.columnName] === 'TIMESTAMP' || columnTypes[criteria.columnName] === 'DATE') {
            if (['BETWEEN', 'NOT BETWEEN'].includes(criteria.operator)) {
                return (
                    <RangePicker style={{ width: '250px' }}
                        size="small"
                        value={getDateValues()}
                        onChange={handleDateChange}
                        placeholder={['Start Date', 'End Date']}
                    />
                );
            } else {
                return (
                    <DatePicker style={{ width: '250px' }}
                        size="small"
                        value={criteria.values && criteria.values[0] ? dayjs(criteria.values[0]) : null}
                        onChange={(date, dateString) => handleDateChange([date], [dateString])}
                        placeholder="Select Date"
                    />
                );
            }
        }

        if (criteria.operator && shouldShowRangeInput(criteria.operator)) {
            return (
                <Flex gap={5}>
                    <Select
                        size="small" style={{ width: '120px' }}
                        value={criteria.values ? criteria.values[0] : undefined}
                        mode="tags"
                        onChange={(value) => {
                            const newValues = [...(criteria.values || [])];
                            newValues[0] = value;
                            onUpdate('values', newValues);
                        }}
                        placeholder="From Value">
                        {Array.isArray(metadata[criteria.columnName]) ?
                            metadata[criteria.columnName].map((value) => (
                                <Option key={value} value={value}>
                                    {value}
                                </Option>
                            ))
                            : null
                        }
                    </Select>
                    <Select
                    mode="tags"
                        size="small" style={{ width: '120px' }}
                        value={criteria.values ? criteria.values[1] : undefined}
                        onChange={(value) => {
                            const newValues = [...(criteria.values || []), undefined];
                            newValues[1] = value;
                            onUpdate('values', newValues);
                        }}
                        placeholder="To Value">
                        {Array.isArray(metadata[criteria.columnName]) ?
                            metadata[criteria.columnName].map((value) => (
                                <Option key={value} value={value}>
                                    {value}
                                </Option>
                            ))
                            : null
                        }
                    </Select>
                </Flex>
            );
        }

        const isMultipleValuesAllowed = ['IN', 'NOT IN'].includes(criteria.operator);

        return (
            <Select
                size="small"
                style={{ width: '250px' }}
                mode="tags"
                allowClear={true}
                value={isMultipleValuesAllowed ? (criteria.values || []) : (criteria.values?.[0] ? [criteria.values[0]] : [])}
                onChange={(values) => {
                    if (isMultipleValuesAllowed) {
                        onUpdate('values', values);
                    } else {
                        onUpdate('values', [values[values.length - 1]]);
                    }
                }}
                placeholder="Enter value or select from list"
            >
                {Array.isArray(metadata[criteria.columnName]) && metadata[criteria.columnName].length > 0
                    ? metadata[criteria.columnName].map((value) => (
                        <Option key={value} value={value}>
                            {value}
                        </Option>
                    ))
                    : null
                }
            </Select>
        );
    };

    return (
        <Flex align="center" style={{ marginBottom: 10 }} gap={10}>
            <Form.Item style={{ flex: 1, marginBottom: 0 }}>
                <Select
                    size="small"
                    value={criteria.columnName}
                    onChange={(value) => {
                        onUpdate('columnName', value);
                        onUpdate('operator', undefined);
                        onUpdate('values', [undefined]);
                        onColumnChange(value);
                    }}
                    showSearch
                    placeholder="Select Column"
                    loading={isColumnLoading}
                    style={{ width: '200px' }}
                >
                    {columnNames.map((column) => (
                        <Option
                            key={column.columnName || column}
                            value={column.columnName || column}
                        >
                            {column.columnName || column}
                        </Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item style={{ flex: 1, marginBottom: 0 }}>
                <Select 
                    size="small"
                    style={{ width: '150px' }}
                    showSearch
                    value={criteria.operator}
                    onChange={(value) => {
                        onUpdate('operator', value);
                        
                        if (['IS NULL', 'IS NOT NULL'].includes(value)) {
                            onUpdate('values', []);
                        } 
                        else if (['BETWEEN', 'NOT BETWEEN'].includes(value)) {
                            onUpdate('values', [undefined, undefined]);
                        }
                        else if (['IN', 'NOT IN'].includes(value)) {
                            onUpdate('values', []);
                        }
                        else {
                            onUpdate('values', [undefined]);
                        }
                    }}
                    placeholder="Operator"
                >
                    {operators.map((op) => (
                        <Option key={op} value={op}>
                            {op}
                        </Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item style={{ flex: 1, marginBottom: 0 }}>
                {renderValueInput()}
            </Form.Item>

            <Button
                type="text"
                danger
                icon={<DeleteOutlined />}
                onClick={onDelete}
            />
        </Flex>
    );
};

export default CriteriaRow;